/**
 * Display helpers for Statistical Significance (Spec 2.2) and shared formatting.
 * Pure functions — unit-tested in significance.test.ts.
 */

export interface BadgeMeta {
  label: string;
  /** Tailwind classes for the badge pill. */
  className: string;
  description: string;
}

/** Map a backend significance badge to display label + styling + tooltip text. */
export const significanceBadgeMeta = (badge: string | null | undefined): BadgeMeta => {
  switch (badge) {
    case 'significant':
      return {
        label: 'Statistically Significant',
        className: 'bg-green-500/20 text-green-400 border-green-500/30',
        description: 'Positive alpha at p < 0.05 with a Sharpe interval that excludes zero.',
      };
    case 'approaching':
      return {
        label: 'Approaching Significance',
        className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
        description: 'Trending toward significance — needs more closed trades to confirm.',
      };
    default:
      return {
        label: 'Insufficient Data',
        className: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
        description: 'Not enough closed trades (or no detectable edge) to make a claim.',
      };
  }
};

/** Format a p-value for display: null → "—", tiny → "< 0.001", else 3 decimals. */
export const formatPValue = (p: number | null | undefined): string => {
  if (p === null || p === undefined || Number.isNaN(p)) return '—';
  if (p < 0.001) return '< 0.001';
  return p.toFixed(3);
};

/** Format a [low, high] confidence interval, or "—" when missing. */
export const formatCI = (
  ci: [number, number] | number[] | null | undefined,
  digits = 2,
): string => {
  if (!ci || ci.length !== 2 || ci.some((v) => v === null || v === undefined || Number.isNaN(v))) {
    return '—';
  }
  return `[${ci[0].toFixed(digits)}, ${ci[1].toFixed(digits)}]`;
};

/**
 * Human sentence for trades-to-significance.
 * Handles already-significant, a finite estimate, and the unreachable/no-effect case.
 */
export const formatTradesToSignificance = (
  current: number,
  additionalNeeded: number | null | undefined,
  alreadySignificant: boolean,
): string => {
  if (alreadySignificant) return 'Significance reached';
  if (additionalNeeded === null || additionalNeeded === undefined) {
    return 'Not reachable at current edge';
  }
  if (additionalNeeded <= 0) return 'Significance reached';
  return `~${additionalNeeded} more trade${additionalNeeded === 1 ? '' : 's'} needed`;
};
